/**
 * Article detail — renders imported HTML content from socialpulse.com.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, ExternalLink, Star, Pencil } from "lucide-react";
import PhotoSubmitModal from "@/components/PhotoSubmitModal";
import ContentPhotoStrip from "@/components/ContentPhotoStrip";
import RelatedFoodItemsStrip from "@/components/RelatedFoodItemsStrip";
import ArticleReaderToolbar from "@/components/reader/ArticleReaderToolbar";
import { resolveSiteSlug } from "@/config/siteConfig";

// Very small markdown → HTML shim for the curated IFT corpus. We only
// need to handle headings, bold/italic, blockquotes, lists, code fences
// and paragraphs. Anything richer already lives in the SocialPulse
// HTML corpus and is passed through untouched.
function mdToHtml(md) {
  if (!md) return "";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Preserve fenced code blocks first.
  const blocks = [];
  md = md.replace(/```([\s\S]*?)```/g, (_m, code) => {
    blocks.push(`<pre><code>${esc(code)}</code></pre>`);
    return `\u0000BLOCK${blocks.length - 1}\u0000`;
  });
  // Inline formatting.
  const inline = (line) => line
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  const out = [];
  let inList = false;
  let inTable = false;
  const flushList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  const flushTable = () => { if (inTable) { out.push("</tbody></table>"); inTable = false; } };
  for (const raw of md.split(/\r?\n/)) {
    const line = raw.trimEnd();
    if (!line) { flushList(); flushTable(); continue; }
    // Headings
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { flushList(); flushTable(); out.push(`<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`); continue; }
    // Blockquotes
    if (line.startsWith(">")) { flushList(); flushTable(); out.push(`<blockquote>${inline(line.replace(/^>\s?/, ""))}</blockquote>`); continue; }
    // Table row
    if (line.startsWith("|") && line.endsWith("|")) {
      const cells = line.slice(1, -1).split("|").map((c) => c.trim());
      // header-divider row?
      if (cells.every((c) => /^:?-{2,}:?$/.test(c))) continue;
      if (!inTable) { out.push('<table class="wofy-md-table"><tbody>'); inTable = true; }
      out.push("<tr>" + cells.map((c) => `<td>${inline(c)}</td>`).join("") + "</tr>");
      continue;
    } else if (inTable) flushTable();
    // Horizontal rule
    if (/^-{3,}$/.test(line)) { flushList(); out.push("<hr />"); continue; }
    // Bullet list item
    if (/^[-*]\s+/.test(line)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${inline(line.replace(/^[-*]\s+/, ""))}</li>`);
      continue;
    }
    flushList();
    out.push(`<p>${inline(line)}</p>`);
  }
  flushList(); flushTable();
  return out.join("\n").replace(/\u0000BLOCK(\d+)\u0000/g, (_m, i) => blocks[+i] || "");
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const isAdmin = !!(user && (user.is_admin || user.is_super_admin));
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [featuring, setFeaturing] = useState(false);
  const bodyRef = useRef(null);
  const isIft = resolveSiteSlug() === "indianfamilytree";

  // Load: IFT-native articles live in /api/articles (markdown ``body``,
  // ``hero_image``, ``author_name``); SocialPulse articles come from
  // /api/sp-articles (rendered ``html``, ``feature_image``, ``author``).
  // We branch on the resolved site, and fall back to the other endpoint
  // on 404 so an IFT-tagged article can still open under a SocialPulse
  // deploy and vice-versa.
  const load = async () => {
    setLoading(true);
    try {
      let data = null;
      if (isIft) {
        try {
          data = (await http.get(`/articles/${slug}`)).data;
        } catch (e) {
          if (e?.response?.status === 404) data = (await http.get(`/sp-articles/${slug}`)).data;
          else throw e;
        }
      } else {
        try {
          data = (await http.get(`/sp-articles/${slug}`)).data;
        } catch (e) {
          if (e?.response?.status === 404) data = (await http.get(`/articles/${slug}`)).data;
          else throw e;
        }
      }
      // Normalise the two schemas so the rest of the page can render
      // one shape without a bunch of ternaries.
      const html = data?.html
        || (data?.body ? mdToHtml(data.body) : "");
      setArticle({
        ...data,
        html,
        feature_image: data?.feature_image || data?.hero_image || null,
        author:        data?.author        || data?.author_name || "",
        published_at:  data?.published_at  || data?.updated_at || data?.created_at || null,
      });
    } catch (e) {
      if (e?.response?.status === 404) setArticle(null);
      else toast.error("Failed to load article");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const toggleFeature = async () => {
    if (!article) return;
    setFeaturing(true);
    try {
      await http.patch(`/sp-articles/${slug}/feature`, { is_featured: !article.is_featured });
      toast.success(article.is_featured ? "Un-featured" : "Featured on Home");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setFeaturing(false); }
  };

  if (loading) return <div className="max-w-3xl mx-auto p-16 text-center text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</div>;
  if (!article) {
    return (
      <div className="max-w-3xl mx-auto p-16 text-center">
        <h1 className="font-display text-3xl mb-3">Article not found</h1>
        <Link to="/articles" className="wofy-btn-primary inline-flex">Browse all articles</Link>
      </div>
    );
  }
  const date = article.published_at ? new Date(article.published_at) : null;

  return (
    <article className="max-w-3xl mx-auto px-6 py-14" data-testid="article-detail">
      <div className="flex items-center justify-between mb-6">
        <Link to="/articles" className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100">
          <ArrowLeft className="w-3.5 h-3.5" /> All articles
        </Link>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={toggleFeature}
              disabled={featuring}
              data-testid="article-feature-toggle"
              className={`text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border`}
              style={{
                borderColor: article.is_featured ? "var(--wofy-primary)" : "var(--wofy-border)",
                background: article.is_featured ? "var(--wofy-primary)" : "transparent",
                color: article.is_featured ? "white" : "var(--wofy-text-soft)",
              }}
            >
              <Star className="w-3.5 h-3.5" /> {article.is_featured ? "Featured on Home" : "Feature"}
            </button>
            <Link
              to={`/articles/${slug}/edit`}
              data-testid="article-edit"
              className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border"
              style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Link>
          </div>
        )}
      </div>

      <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>ARTICLE{article.language ? ` · ${article.language_native || article.language.toUpperCase()}` : ""}</div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight mb-4" data-testid="article-title">
        {article.title}
      </h1>

      {/* Language switcher — surfaced when the article has translated
          editions or is itself a translation. Clicking a chip navigates
          to the corresponding sibling article. English is inferred from
          the ``parent_slug`` field for translated editions. */}
      {(article.other_language_editions?.length || article.parent_slug) && (
        <div className="mb-3 flex flex-wrap gap-1.5" data-testid="article-language-switcher">
          <span className="text-[10px] font-mono uppercase tracking-wider opacity-60 self-center mr-1">
            Read in:
          </span>
          {/* English root — either self (this doc is the parent) or a
              backlink from a translated edition. */}
          {article.parent_slug ? (
            <Link
              to={`/articles/${article.parent_slug}`}
              className="text-[11px] px-2.5 py-1 rounded-full border hover:bg-slate-100"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="article-lang-en"
            >
              English
            </Link>
          ) : (
            <span
              className="text-[11px] px-2.5 py-1 rounded-full border text-white"
              style={{ borderColor: "var(--wofy-primary)", background: "var(--wofy-primary)" }}
            >
              English
            </span>
          )}
          {(article.other_language_editions || []).map((ed) => (
            <Link
              key={ed.slug}
              to={`/articles/${ed.slug}`}
              className={`text-[11px] px-2.5 py-1 rounded-full border hover:bg-slate-100 ${article.slug === ed.slug ? "text-white" : ""}`}
              style={{
                borderColor: article.slug === ed.slug ? "var(--wofy-primary)" : "var(--wofy-border)",
                background: article.slug === ed.slug ? "var(--wofy-primary)" : "transparent",
              }}
              data-testid={`article-lang-${ed.language}`}
            >
              {ed.language_native || ed.language.toUpperCase()}
            </Link>
          ))}
          {/* When THIS doc IS a translation, also render sibling translations
              declared on the parent — we don't have them locally, so keep
              the switcher lightweight (only English + this language + a
              tiny CTA). */}
        </div>
      )}

      <div className="text-xs mb-4" style={{ color: "var(--wofy-text-soft)" }}>
        {date && date.toLocaleDateString(undefined, { dateStyle: "long" })}
        {article.author && ` · ${article.author}`}
        {article.source_url && (
          <>
            {" · "}
            <a href={article.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 underline">
              View original <ExternalLink className="w-3 h-3" />
            </a>
          </>
        )}
      </div>

      {article.tags && article.tags.length > 0 && (
        <div className="mb-8 flex flex-wrap gap-1.5">
          {article.tags.map((t) => (
            <Link
              key={t}
              to={`/articles?tag=${encodeURIComponent(t)}`}
              className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border hover:bg-[color:var(--wofy-primary)] hover:text-white transition-colors"
              style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
            >
              {t}
            </Link>
          ))}
        </div>
      )}

      {article.feature_image && String(article.feature_image).trim() && (
        <img
          src={article.feature_image}
          alt={article.title}
          className="w-full aspect-[16/9] object-cover rounded-2xl mb-6"
          loading="lazy"
        />
      )}

      {/* Photo submission CTA + top user photos */}
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap p-3 rounded-xl" style={{ background: "var(--wofy-surface)" }}>
        <div className="text-xs opacity-80 flex-1 min-w-[220px]">
          📷 <b>Share Your Photos</b> — landmarks, scenery, plants, food, wildlife, or a special moment. Every approved photo is credited to you.
        </div>
        <PhotoSubmitModal
          targetType="article"
          targetSlug={slug}
          targetTitle={article.title}
        />
      </div>
      <ContentPhotoStrip targetType="article" targetSlug={slug} slot="top" max={4} />

      <ArticleReaderToolbar
        bodyRef={bodyRef}
        title={article.title}
        source_lang={article.language || "en"}
        storageKey={`article:${slug}`}
      />

      <div
        ref={bodyRef}
        className="prose-article"
        data-testid="article-body"
        dangerouslySetInnerHTML={{ __html: article.content_html || article.html || "" }}
      />

      {/* Middle & bottom user photos */}
      <ContentPhotoStrip targetType="article" targetSlug={slug} slot="middle" max={4} />
      <ContentPhotoStrip targetType="article" targetSlug={slug} slot="bottom" max={4} />

      {/* Related food items — cross-linking hub */}
      <RelatedFoodItemsStrip articleSlug={slug} />

      <div className="mt-16 border-t pt-6 text-xs" style={{ borderColor: "var(--wofy-line)", color: "var(--wofy-text-soft)" }}>
        {article.source === "in-house" ? (
          <>Published on Social Pulse.</>
        ) : (
          <>Originally published on{" "}
            <a href="https://www.socialpulse.com" target="_blank" rel="noopener noreferrer" className="underline">
              socialpulse.com
            </a>.</>
        )}
      </div>
    </article>
  );
}

